import { AppNS } from 'app';
import { DB_DATE_FORMAT_STRING, DB_DATETIME_FORMAT_STRING, TIMESTAMP_FORMAT_STRING } from 'app/helpers/parseDate';
import { parseMoney } from 'app/helpers/parseMoney';
import { orderBy } from 'lodash-es';
import moment from 'moment';

export const parseTimeTo = (timeTo: string): string => {
  return timeTo === '0:00' ? '23:59:59' : timeTo;
};

export const getHourFormatted = (hour: string): string => {
  return moment(`2000-01-01 ${hour}`, DB_DATETIME_FORMAT_STRING).format('h:mma');
};

export const getHourFormatted24 = (hour: string): string => {
  return moment(`2000-01-01 ${hour}`, DB_DATETIME_FORMAT_STRING).format('H:mm');
};

export const getDateFormatted = (date: string): string => {
  return moment(date, DB_DATE_FORMAT_STRING).format('MMM D, YYYY');
};

export const getDateFormattedForInvoice = (date: string, format: string = 'MMMM D, YYYY'): string => {
  return moment.utc(date, TIMESTAMP_FORMAT_STRING).local().format(format);
};

export const isEventInProgress = (date: string, timeFrom: string, timeTo: string): boolean => {
  const parsedTimeTo = parseTimeTo(timeTo);

  const from = moment(`${date} ${timeFrom}`, DB_DATETIME_FORMAT_STRING);
  const to = moment(`${date} ${parsedTimeTo}`, DB_DATETIME_FORMAT_STRING);

  return moment().isBetween(from, to);
};

export const isEventTimeValid = (timeFrom: string, timeTo: string): boolean => {
  const from = moment(`2000-01-01 ${timeFrom}`, DB_DATETIME_FORMAT_STRING);
  const to = moment(`2000-01-01 ${timeTo}`, DB_DATETIME_FORMAT_STRING);

  return to.isAfter(from);
};

export const isEventPast = (date: string, timeTo: string): boolean => {
  const parsedTimeTo = parseTimeTo(timeTo);
  const to = moment(`${date} ${parsedTimeTo}`, DB_DATETIME_FORMAT_STRING);
  return moment().isAfter(to);
};

export const getEventListItems = (events: Array<AppNS.JobEventReport>): Array<AppNS.ProjectEventListItem> => {
  const sorted = sortEventsByDate(events);

  const output = sorted.map((item: AppNS.JobEventReport) => {
    return {
      id: item.id,
      date: getDateFormatted(item.date),
      timeTo: item.timeTo,
      timeSpan: `${getHourFormatted(item.timeFrom)} - ${getHourFormatted(parseTimeTo(item.timeTo))}`,
      total: item.amountForClient || 0,
      state: item.state || 'upcoming',
      eventInspections: item.eventInspections,
    };
  });
  return output as Array<AppNS.ProjectEventListItem>;
};

export const getEventListItemsForAgencyBooking = (
  events: Array<AppNS.JobEventReport>,
): Array<AppNS.ProjectEventListItem> => {
  const sorted = sortEventsByDate(events);
  const output = sorted.map((item: AppNS.JobEventReport) => {
    return {
      id: item.id,
      date: getDateFormatted(item.date),
      timeSpan: `${getHourFormatted(item.timeFrom)} - ${getHourFormatted(parseTimeTo(item.timeTo))}`,
      total: item.amountForAgency || 0,
      totalForClient: item.amountForClient || 0,
      state: item.state,
      eventInspections: item.eventInspections,
    };
  });
  return output as Array<AppNS.ProjectEventListItem>;
};

export const getEventListItemsForAgencyInvoice = (
  events: Array<AppNS.JobEventReport>,
): Array<AppNS.ProjectEventListItem> => {
  const sorted = sortEventsByDate(events);
  const output = sorted
    .map((item: AppNS.JobEventReport) => {
      if (item.state !== 'ready_to_invoice') {
        return null;
      }

      return {
        id: item.id,
        date: getDateFormatted(item.date),
        timeSpan: `${getHourFormatted(item.timeFrom)} - ${getHourFormatted(parseTimeTo(item.timeTo))}`,
        total: item.amountForAgency || 0,
        totalForClient: item.amountForClient || 0,
        state: item.state,
      };
    })
    .filter((item) => {
      return item;
    });
  return output as Array<AppNS.ProjectEventListItem>;
};

export const getEventsSummaryAgency = (
  eventsList: Array<AppNS.ProjectEventListItem>,
  invoicesList: Array<AppNS.ProjectInvoice>,
): AppNS.JobEventsSummaryForAgency => {
  const output = {
    eventsNumber: 0,
    total: 0,
    totalForClient: 0,
    totalForAgency: 0,
  };
  eventsList.forEach((item) => {
    if (item.state?.includes('deleted_by') || item.state?.includes('cancelled')) {
      return;
    }
    output.eventsNumber += 1;
    output.total += item.total;
    output.totalForClient += item.totalForClient || 0;
    output.totalForAgency += item.total || 0;
  });

  invoicesList.forEach((item) => {
    if (item.state?.includes('deleted')) {
      return;
    }
    output.totalForClient += item.amountForClient;
    output.totalForAgency += item.amountForAgency;
  });

  return output as AppNS.JobEventsSummaryForAgency;
};

export const getEventsSummaryClient = (
  eventsList: Array<AppNS.ProjectEventListItem>,
  invoicesList: Array<AppNS.ProjectInvoice>,
): AppNS.JobEventsSummary => {
  const output = {
    eventsNumber: 0,
    total: 0,
  };
  eventsList.forEach((item) => {
    if (item.state?.includes('deleted_by') || item.state?.includes('cancelled')) {
      return;
    }
    output.eventsNumber += 1;
    output.total += item.total;
  });
  invoicesList.forEach((item) => {
    if (item.state?.includes('deleted')) {
      return;
    }
    output.total += item.amountForClient;
  });

  return output as AppNS.JobEventsSummary;
};

export const getTimeSpan = (project: AppNS.ProjectLead): string => {
  if (project.startDate) {
    if (project.startDate === project.endDate) {
      return `${moment(project.startDate, DB_DATE_FORMAT_STRING).format('MMM D, YYYY')}`;
    }
    return `${moment(project.startDate, DB_DATE_FORMAT_STRING).format('MMM D, YYYY')} - ${moment(
      project.endDate,
      DB_DATE_FORMAT_STRING,
    ).format('MMM D, YYYY')}`;
  }
  return '';
};

export const eventsCanBeDeleted = (allEvents: Array<AppNS.ProjectEventListItem>): boolean => {
  // can delete if at least one event is completed
  // or upcoming/in progress events length > 1
  const completed = allEvents.filter((ev) => ev?.state === 'completed');
  const upcoming = allEvents.filter((ev) => ev?.state === 'upcoming' || ev?.state === 'in_progress');
  if (completed.length > 0 || upcoming.length > 1) {
    return true;
  }
  return false;
};

export const eventStatuses: Map<AppNS.JobReportState, string> = new Map([
  ['upcoming', 'Scheduled'],
  ['in_progress', 'In Progress'],
  ['in_qa', 'In QA'],
  ['finalized_report', 'Finalized'],
  ['ready_for_issuance', 'Ready for Issuance'],
  ['invoiced', 'Invoiced'],
  ['ready_to_invoice', 'Ready to Invoice'],
  ['cancelled_by_agency', 'Cancelled'],
  ['cancelled_by_client', 'Cancelled'],
  ['cancelled_by_admin', 'Cancelled'],
  ['payment_pending', 'Pending'],
  ['payment_failed', 'Payment Failed'],
  ['completed', 'Completed'],
  ['paid', 'Paid'],
]);

export const invoiceClientStatuses: Map<AppNS.ProjectInvoiceState, string> = new Map([
  ['pending', 'New Request'],
  ['payment_pending', 'Pending'],
  ['payment_failed', 'Failed'],
  ['paid', 'Paid'],
  ['deleted', 'Deleted'],
]);

export const invoiceAgencyStatuses: Map<AppNS.ProjectInvoiceState, string> = new Map([
  ['pending', 'Pending'],
  ['payment_pending', 'Pending'],
  ['payment_failed', 'Failed'],
  ['paid', 'Paid'],
  ['deleted', 'Deleted'],
]);

export const getEventStatus = (status: AppNS.JobReportState, type: string): string => {
  if (
    (type === 'agency' && status === 'cancelled_by_agency') ||
    (type === 'client' && status === 'cancelled_by_client') ||
    status === 'cancelled'
  ) {
    return 'Cancelled';
  }
  if (type === 'client' && status === 'completed') {
    return 'Fulfilled';
  }
  return eventStatuses.get(status) || '';
};

export const getInvoiceStatus = (status: AppNS.ProjectInvoiceState, type: string): string => {
  if (type === 'client') {
    return invoiceClientStatuses.get(status) || '';
  }
  return invoiceAgencyStatuses.get(status) || '';
};

const sortEventsByDate = (events: Array<AppNS.JobEventReport>): Array<AppNS.JobEventReport> => {
  return orderBy(events, (item: AppNS.JobEventReport) => {
    return moment(item.date, DB_DATE_FORMAT_STRING).valueOf();
  });
};

export const jobCanBeDeleted = (events: Array<AppNS.ProjectEventListItem>): boolean => {
  return events.length === 0;
  // // cannot delete if at least one event report is in state "finalized_report"
  // return !events.some((ev) => ev?.report?.state === 'finalized_report');
};

export const getEventTotal = (item: AppNS.ProjectEventListItem) => {
  if (item.total > 0) {
    return parseMoney(item.total * 100);
  }
  return parseMoney(0);
};

export const INSPECTION_STATUS = [
  { id: 'scheduled', name: 'Scheduled' },
  { id: 'approved', name: 'Third Party Completed' },
  { id: 'partially_approved', name: 'Third Party Partial Submission' },
  { id: 'failed', name: 'Third Party Failed' },
  { id: 'cancelled', name: 'Cancelled' },
  // { id: 'rescheduled', name: 'Rescheduled' },
];

export const isReportCompleted = (currentState: string): boolean => {
  const statesAfterQa = [
    'ready_to_invoice',
    'invoiced',
    'finalized_report',
    'payment_pending',
    'payment_failed',
    'paid',
  ];
  return statesAfterQa.includes(currentState);
};

export const sortInspections = (
  data: Array<AppNS.Inspection>,
): Array<{ permitName: string; eventInspections: Array<AppNS.Inspection> }> => {
  const output = [] as Array<{
    permitName: string;
    eventInspections: Array<AppNS.Inspection>;
  }>;
  const outputObj = {} as {
    [key: string]: {
      permitName: string;
      eventInspections: Array<AppNS.Inspection>;
    };
  };
  data.forEach((item) => {
    const index = Number(item.permit?.id) || 0;
    outputObj[index] = {
      permitName: `${item.permit?.inspectionGroup} ${item.permit?.permitNumber}`,
      eventInspections: [...(outputObj[index]?.eventInspections || []), item],
    };
  });

  for (const key in outputObj) {
    if (Object.prototype.hasOwnProperty.call(outputObj, key)) {
      output.push(outputObj[key]);
    }
  }

  return output;
};
